import {
  ClipboardDocumentIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/20/solid'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import type { ChargerSetup } from 'cleverev-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useParams } from 'react-router'
import { toast } from 'sonner'
import { chargerAPI } from '#/api/charger'
import { Button } from '#/components/ui/button'
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm
} from '#/components/ui/description-list'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '#/components/ui/dropdown-menu'
import { Input } from '#/components/ui/input'
import { Separator } from '#/components/ui/separator'
import { Heading, Subheading } from '#/components/ui/typography'
import { copy } from '#/lib/copy'
import { DeleteChargerModal } from '../@modal/delete/page'
import { TrashIcon } from 'lucide-react'


export default function ChargerPage() {
  const { t } = useTranslation([ 'chargers', 'general' ])
  const params = useParams() as { organization: string; charger: string }
  const queryClient = useQueryClient()
  const [ isEditing, setIsEditing ] = useState(false)
  const [ localCharger, setLocalCharger ] = useState<Partial<ChargerSetup>>({})
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState<{ serialNumber: string } | null>(null)

  const { data: charger } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization, 'chargers', params.charger ],
    queryFn: () => chargerAPI.get(params.charger)
  })

  // Copy charger id and notify
  function copyChargerId(charger: ChargerSetup) {
    copy(charger.serialNumber)
    toast.info(t('view_charger.id_copied'))
  }

  function startEditing() {
    setIsEditing(true)
    setLocalCharger(charger)
  }

  function cancelEditing() {
    setIsEditing(false)
    setLocalCharger({})
  }

  function renderField(key: keyof ChargerSetup, fallback = '-') {
    const value = isEditing
      ? localCharger[key] ?? ''
      : charger[key] ?? fallback

    if (!isEditing) return <>{value}</>

    return (
      <Input
        className="w-full border px-2 rounded"
        type="text"
        value={value}
        onChange={e =>
          setLocalCharger({ ...localCharger, [key]: e.target.value })
        }
      />
    )
  }

  async function handleSave() {
    try {
      await chargerAPI.update(params.charger, localCharger)
      toast.success(t('general:saved_successfully'))
      setIsEditing(false)
      queryClient.setQueryData(
        [ 'organizations', params.organization, 'chargers', localCharger.serialNumber ],
        localCharger
      )
    } catch {
      toast.error(t('general:save_failed'))
    }
  }

  return (
    <div className="flex grow flex-col gap-4 items-start">

      <Outlet />

      {isDeleteModalOpen !== null
      && (<DeleteChargerModal
        serialNumber={isDeleteModalOpen.serialNumber}
        onClose={() => setIsDeleteModalOpen(null)}
      />)}

      <div className="flex w-full items-end justify-between gap-4">
        <Heading>{`${t('charger_list.charger')} ${charger.serialNumber}`}</Heading>
        <div className="flex flex-row gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button variant="ghost" size="icon">
                <EllipsisVerticalIcon className="text-tertiary" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => copyChargerId(charger)}>
                <ClipboardDocumentIcon className="text-tertiary" />
                {t('view_charger.menu.copy_id')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={isEditing ? handleSave : startEditing}>
                <ClipboardDocumentIcon className="text-tertiary" />
                {isEditing ? t('general:save') : t('general:edit')}
              </DropdownMenuItem>
              {isEditing && (
                <DropdownMenuItem onClick={cancelEditing}>
                  {t('general:cancel')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() =>
                setIsDeleteModalOpen({
                  serialNumber: charger.serialNumber
                })}>
                <TrashIcon className="text-tertiary" />
                {t('charger_list.menu.remove')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Separator />

      <div className="flex w-full items-center justify-between">
        <Subheading>{t('view_charger.details')}</Subheading>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={isEditing ? handleSave : startEditing}
          >
            {isEditing ? t('general:save') : t('general:edit')}
          </Button>
          {isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={cancelEditing}
            >
              {t('general:cancel')}
            </Button>
          )}
        </div>
      </div>

      <DescriptionList className="w-full">
        <DescriptionTerm>
          {t('fields.serialNumber')}
        </DescriptionTerm>
        <DescriptionDetails>
          {charger.serialNumber}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.vendor')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('vendor')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.model')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('model')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.initialFirmwareVersion')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('initialFirmwareVersion')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.wifiSSID')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('wifiSSID')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.wifiPassword')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('wifiPassword')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.setupUrl')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('setupUrl')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.setupAdminPassword')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('setupAdminPassword')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.cleverevConnectionId')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('cleverevConnectionId')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.cleverevConnectionUrl')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('cleverevConnectionUrl')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.cleverevStickerId')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('cleverevStickerId')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.cleverevRfidFriendlyId')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('cleverevRfidFriendlyId')}
        </DescriptionDetails>
        <DescriptionTerm>
          {t('fields.organizationId')}
        </DescriptionTerm>
        <DescriptionDetails>
          {renderField('organizationId')}
        </DescriptionDetails>
      </DescriptionList>

    </div>
  )
}
